import moment from 'moment';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { ProjectId, User, UserType } from 'api/models';
import { StatusUpdate, StatusUpdateState } from 'api/status-updates';
import { DocumentProvider } from 'components/organisms/document/document-store';
import { useGrid } from 'components/organisms/templates/projects/plan/list-view/grid';
import { SquadProvider } from 'contexts/providers/squad';
import {
  putStatusUpdates,
  selectActiveStatusUpdate,
  selectStatusUpdates,
  setActiveStatusUpdate,
} from 'data/status-updates';
import { useGetAllActiveUsersMap } from 'hooks/use-idb';
import { useUser } from 'hooks/use-user';
import { getProjectStatusUpdatesUrl } from 'utils/urls';
import { Suggestion } from 'vendor/ckeditor/plugins/mention/types';
import { StatusUpdateEditor } from '../editor';
import { StatusUpdateTabs } from '../tabs';
import { StatusUpdateList } from './status-update-list';
import {
  StatusUpdateFilters,
  StatusUpdateFilterTabs,
} from './status-update-tabs';
import { StatusUpdateDrawer, SUContainer } from './styles';
import { useStatusUpdateUserMention } from './use-su-user-mention';

interface Props {
  items: StatusUpdate[];
  activeStatusUpdateId: null | number;
}

export interface FilteredUpdates {
  all: StatusUpdate[];
  sent: StatusUpdate[];
  drafts: StatusUpdate[];
}

export const StatusUpdates: FC<Props> = ({ items, activeStatusUpdateId }) => {
  const { projectId: projectIdStr, statusUpdateId } =
    useParams<{ projectId: string; statusUpdateId: string }>();
  const projectId = Number(projectIdStr);
  const statusUpdates = useSelector(selectStatusUpdates);
  const activeStatusUpdate = useSelector(selectActiveStatusUpdate);
  const dispatch = useDispatch();
  const onUserMention = useStatusUpdateUserMention(ProjectId(projectId));
  const users = useGetAllActiveUsersMap();
  const [tab, onTabChange] = useState(StatusUpdateFilters.All);
  const { gridApi, onGridReady } = useGrid();
  const [sideTabVisiblity, setSideTabVisiblity] = useState(false);
  const [isDrawerVisible, setDrawerVisiblity] = useState(false);
  const userInfo = useUser();
  const history = useHistory();
  const handle = useFullScreenHandle();

  useEffect(() => {
    if (activeStatusUpdateId && !isDrawerVisible) {
      setDrawerVisiblity(true);
    }

    if (isDrawerVisible && !statusUpdateId) {
      setDrawerVisiblity(false);
    }
  }, [activeStatusUpdateId, isDrawerVisible, statusUpdateId]);

  useEffect(() => {
    if (activeStatusUpdate?.statusUpdateId !== activeStatusUpdateId) {
      const currentStatusUpdate = statusUpdates.find(
        (update) => update.statusUpdateId === Number(activeStatusUpdateId),
      );

      if (currentStatusUpdate) {
        dispatch(setActiveStatusUpdate(currentStatusUpdate));
      }
    }
  }, [activeStatusUpdate, activeStatusUpdateId, dispatch, statusUpdates]);

  const filteredUpdates = useMemo(() => {
    const sortedUpdates = items.slice().sort((update, nextUpdate) => {
      const updateStamp =
        update.state === StatusUpdateState.Published
          ? update.publishedAt
          : update.updatedAt;

      const nextUpdateStamp =
        nextUpdate.state === StatusUpdateState.Published
          ? nextUpdate.publishedAt
          : nextUpdate.updatedAt;

      return moment(nextUpdateStamp).diff(moment(updateStamp));
    });

    return {
      all: sortedUpdates,
      sent: sortedUpdates.filter(
        (update) => update.state === StatusUpdateState.Published,
      ),
      drafts: sortedUpdates.filter(
        (update) => update.state === StatusUpdateState.Draft,
      ),
    };
  }, [items]);

  const onMention = (selected, addMention) => {
    const collaborators = activeStatusUpdate?.collaborators || ([] as User[]);
    const user = users[selected.objectId] as User;
    const isExternalUser = !!(user && user.userType === UserType.External);

    let hideAddCollabAlert;
    const isSuggestionInCollaborators = collaborators.find(
      (collaborator) => collaborator.userId === selected.objectId,
    );

    // Don't show alert for published updates.
    if (activeStatusUpdate?.state === StatusUpdateState.Published) {
      hideAddCollabAlert = true;
    }
    // If status update is private, allow customer mention as it is and show alert for native users only
    else if (activeStatusUpdate?.private && !isSuggestionInCollaborators) {
      hideAddCollabAlert = isExternalUser;
    } else {
      // show alert if mentioned user is not a collaborator
      hideAddCollabAlert = !!isSuggestionInCollaborators;
    }

    const handleCallback = (suggestion: Suggestion) => {
      if (!hideAddCollabAlert) {
        const newCollaborators = user
          ? [...(activeStatusUpdate?.collaborators || []), user]
          : activeStatusUpdate?.collaborators || [];

        const updatedStatus = {
          ...(activeStatusUpdate as StatusUpdate),
          collaborators: newCollaborators,
        };

        dispatch(setActiveStatusUpdate(updatedStatus));
        dispatch(
          putStatusUpdates({
            statusUpdate: updatedStatus,
          }),
        );
      }
      addMention(suggestion);
    };

    onUserMention(selected, handleCallback, {
      hideAddCollabAlert,
    });
  };

  const onClose = () => {
    setDrawerVisiblity(false);
    const redirectUrl = getProjectStatusUpdatesUrl(ProjectId(projectId));

    handle.exit();
    history.push(redirectUrl);
    const row = gridApi?.getRowNode(String(activeStatusUpdate?.statusUpdateId));

    if (row) {
      row.setSelected(false);
      gridApi?.refreshCells({
        rowNodes: [row],
        force: true,
      });
    }
  };

  return activeStatusUpdate ? (
    <SquadProvider projectId={activeStatusUpdate?.project.projectId}>
      {userInfo.user.userType === UserType.Native ||
      userInfo.user.userType === UserType.Partner ? (
        <StatusUpdateFilterTabs
          filteredTabData={filteredUpdates}
          tab={tab}
          onChange={onTabChange}
        />
      ) : null}

      <StatusUpdateList
        projectId={projectId}
        activetab={
          userInfo.user.userType === UserType.Native ||
          userInfo.user.userType === UserType.Partner
            ? tab
            : StatusUpdateFilters.Sent
        }
        filteredUpdates={filteredUpdates}
        setDrawerVisiblity={setDrawerVisiblity}
        onGridReady={onGridReady}
        gridApi={gridApi}
      />

      <StatusUpdateDrawer
        className="status-update-drawer"
        large={sideTabVisiblity}
        visible={isDrawerVisible}
        closable={false}
        destroyOnClose
      >
        <FullScreen handle={handle}>
          <SUContainer>
            <DocumentProvider>
              <StatusUpdateEditor
                onTabChange={onTabChange}
                onUserMention={onMention}
                gridApi={gridApi}
                handleFullScreen={handle}
              />
              <StatusUpdateTabs
                handleConversationUserMention={onMention}
                setSideTabVisiblity={setSideTabVisiblity}
                sideTabVisiblity={sideTabVisiblity}
                onClose={onClose}
              />
            </DocumentProvider>
          </SUContainer>
        </FullScreen>
      </StatusUpdateDrawer>
    </SquadProvider>
  ) : null;
};
